/**
 * @module services/snomed-coding
 * @description UC-REG-288 — Cliente del esquema de codificación SNOMED CT de
 *              órdenes de imagen (`/snomed-coding/*` del API). El estado se
 *              consulta una vez por sesión y se cachea en memoria: si el
 *              tenant no tiene el flag o la organización está en modo
 *              `catalog`, el formulario de imágenes sigue exactamente como hoy.
 */
import api from "./api";

const endpoint = "/snomed-coding";

/** @type {Promise<Object>|null} cache de la promesa de estado (una por sesión) */
let statusPromise = null;

/** @description Estado "apagado" que se usa ante cualquier error (compatibilidad). */
export const DISABLED_STATUS = Object.freeze({
  tenantEnabled: false,
  mode: "catalog",
  effectiveMode: "catalog",
  aiConfigured: false,
  conceptsCount: 0,
  reason: "unavailable",
});

const snomedCodingService = {
  /**
   * @description Estado del esquema para la organización del usuario logueado.
   *              Nunca rechaza: ante error (API vieja, 404, red) devuelve
   *              DISABLED_STATUS para que el Dashboard caiga al selector actual.
   * @param {boolean} [force=false] - ignorar el cache de sesión
   * @returns {Promise<Object>} { tenantEnabled, mode, effectiveMode, aiConfigured, conceptsCount, reason }
   */
  getStatus(force = false) {
    if (!statusPromise || force) {
      statusPromise = api
        .get(`${endpoint}/status`, { getErrorMessage: () => null })
        .then(({ data }) =>
          data && typeof data === "object" ? data : DISABLED_STATUS
        )
        .catch(() => DISABLED_STATUS);
    }
    return statusPromise;
  },

  /** @description Limpia el cache de estado (logout / cambio de organización). */
  resetStatus() {
    statusPromise = null;
  },

  /**
   * @description Resuelve un texto libre a una sugerencia SNOMED CT.
   * @param {string} text - pedido del médico ("eco de panza")
   * @returns {Promise<Object>} { decisionId, status, layer, requiresConfirmation, suggestion, alternatives, ambiguity, mode }
   */
  resolve(text) {
    return api.post(`${endpoint}/resolve`, { text }).then(({ data }) => data);
  },

  /**
   * @description Registra la elección del médico sobre una resolución.
   * @param {string} decisionId
   * @param {{sctid?: string, note?: string}} choice - sin `sctid` = texto libre (pendiente)
   * @returns {Promise<Object>} decisión actualizada
   */
  confirm(decisionId, choice = {}) {
    return api
      .post(`${endpoint}/decisions/${decisionId}/confirm`, choice)
      .then(({ data }) => data);
  },

  /**
   * @description Búsqueda directa de conceptos (sin IA) para "Elegir otra".
   * @param {string} search
   * @param {number} [limit=10]
   * @returns {Promise<Array>} candidatos { sctid, display, fsn, category, score }
   */
  searchConcepts(search, limit = 10) {
    return api
      .get(`${endpoint}/concepts`, { params: { search, limit } })
      .then(({ data }) => (Array.isArray(data) ? data : []));
  },

  /**
   * @description Pendientes de codificar de la organización (admin).
   * @param {{limit?: number, offset?: number}} [params]
   * @returns {Promise<{docs: Array, total: number}>}
   */
  getPending(params = {}) {
    return api.get(`${endpoint}/pending`, { params }).then(({ data }) => data);
  },

  /**
   * @description El admin médico resuelve un pendiente.
   * @param {string} decisionId
   * @param {{sctid: string, note?: string}} resolution
   * @returns {Promise<Object>}
   */
  resolvePending(decisionId, resolution) {
    return api
      .put(`${endpoint}/pending/${decisionId}/resolve`, resolution)
      .then(({ data }) => data);
  },

  /**
   * @description Auditoría de resoluciones (admin).
   * @param {{status?: string, layer?: string, limit?: number, offset?: number}} [params]
   * @returns {Promise<{docs: Array, total: number}>}
   */
  getDecisions(params = {}) {
    return api
      .get(`${endpoint}/decisions`, { params })
      .then(({ data }) => data);
  },

  /**
   * @description Recalcula embeddings del catálogo (admin).
   * @param {boolean} [force=false]
   * @returns {Promise<{total:number, indexed:number, skipped:number, failed:number}>}
   */
  reindex(force = false) {
    return api.post(`${endpoint}/reindex`, { force }).then(({ data }) => data);
  },
};

export default snomedCodingService;
